// Two dialogs for the certified-library admin lifecycle actions:
//   FlagForReviewModal — captures a reason then transitions the
//                        template to under_review.
//   DeprecateTemplateModal — captures an effective date (asOf) and an
//                        optional successor template, then deprecates.
//
// These replace the window.prompt / window.confirm placeholders that
// shipped with PR #242. Same hooks (useFlagForReview / useDeprecate);
// only the UX surface changes.
//
// Why two modals (rather than one shared dialog with variants):
// the fields are different enough that branching inside one component
// hurts readability more than it saves. Both modals share the same
// open/close pattern: render via `open` prop, parent owns the "which
// template are we acting on" state.
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogClose,
  Select,
} from "../index";
import {
  useAssignPeerReviewer,
  useDeprecate,
  useFlagForReview,
  useTemplateDependents,
  type CertifiedTemplateRow,
} from "../../data/templatesQuery";

const TEMPLATE_GUILDS = {
  SAG: "SAG-AFTRA",
  DGA: "DGA",
  WGA: "WGA",
  IATSE: "IATSE",
  AFM: "AFM",
  PGA: "PGA",
} as const;

const TEMPLATE_DEAL_TYPES = {
  feature_film: "Feature film",
  tv_series: "TV series",
  streaming: "Streaming",
  documentary: "Documentary",
  commercial: "Commercial",
  short_form: "Short form",
} as const;

// The name a reviewer recognizes: the uploaded title when set, else a
// readable guild/deal-type label. Never the opaque id.
function prettyLabel(t: CertifiedTemplateRow): string {
  const title = t.title?.trim();
  if (title !== undefined && title.length > 0) return title;
  return `${TEMPLATE_GUILDS[t.guild]} · ${TEMPLATE_DEAL_TYPES[t.dealType]}`;
}

// ────────────────────────────────────────────────────────────────────
// FlagForReviewModal
// ────────────────────────────────────────────────────────────────────

export type FlagForReviewModalProps = {
  /** Template being flagged; null hides the dialog. */
  template: CertifiedTemplateRow | null;
  onClose: () => void;
};

export function FlagForReviewModal({
  template,
  onClose,
}: FlagForReviewModalProps): JSX.Element | null {
  const flag = useFlagForReview();
  const [reason, setReason] = useState("");

  // Reset the form whenever the dialog is (re-)opened with a different
  // template, AND clear stale mutation state from a previous attempt.
  useEffect(() => {
    if (template === null) return;
    setReason("Periodic review");
    flag.reset();
    // Key on template.id so swapping the modal between two cards
    // resets the form. flag.reset is stable across renders.
  }, [template?.id]);

  if (template === null) return null;

  const trimmed = reason.trim();
  const canSubmit = trimmed.length >= 3 && !flag.isPending;

  const close = (): void => {
    flag.reset();
    onClose();
  };

  const submit = (): void => {
    if (!canSubmit) return;
    flag.mutate(
      {
        templateId: template.id,
        tenantId: template.tenantId,
        reason: trimmed,
      },
      {
        onSuccess: () => {
          flag.reset();
          onClose();
        },
      }
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) close();
      }}
      title={`Flag ${prettyLabel(template)} for review`}
      description="Moves this template to under_review. Drafters can still resolve against it for new contracts; the next certification cycle resets the state to certified or re_certified."
    >
      <div className="tpl__modal__row">
        <label htmlFor="tpl-flag-reason">Reason</label>
        <textarea
          id="tpl-flag-reason"
          data-testid="tpl-flag-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Updated WGA MBA Article 51 requires revisiting"
        />
        <div className="tpl__modal__hint">
          {trimmed.length < 3
            ? `${3 - trimmed.length} more character${
                3 - trimmed.length === 1 ? "" : "s"
              } needed`
            : "Logged on the template's audit trail."}
        </div>
      </div>
      {flag.isError && (
        <div className="tpl__modal__error" role="alert">
          {flag.error?.message ?? "Failed to flag template"}
        </div>
      )}
      <div className="tpl__modal__ft">
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button
          onClick={submit}
          disabled={!canSubmit}
          data-testid="tpl-flag-submit"
        >
          {flag.isPending ? "Flagging…" : "Flag for review"}
        </Button>
      </div>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// BulkFlagModal — capture a single reason, then flag every selected
// template for review. Presentational: the screen owns the selection +
// the sequential mutation loop and passes busy/error in. Replaces the
// window.prompt bulk-flag placeholder.
// ────────────────────────────────────────────────────────────────────

export type BulkFlagModalProps = {
  /** Open when > 0 templates are selected and the user clicked the action. */
  open: boolean;
  /** How many templates the reason will be applied to. */
  count: number;
  busy: boolean;
  error: string | null;
  onConfirm: (reason: string) => void;
  onClose: () => void;
};

export function BulkFlagModal({
  open,
  count,
  busy,
  error,
  onConfirm,
  onClose,
}: BulkFlagModalProps): JSX.Element | null {
  const [reason, setReason] = useState("Periodic review");

  useEffect(() => {
    if (open) setReason("Periodic review");
  }, [open]);

  if (!open) return null;

  const trimmed = reason.trim();
  const canSubmit = trimmed.length >= 3 && !busy;
  const noun = `${count} template${count === 1 ? "" : "s"}`;

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={`Flag ${noun} for review`}
      description="Moves the selected templates to under_review. Drafters can still resolve against them for new contracts; the next certification cycle resets each to certified or re_certified."
    >
      <div className="tpl__modal__row">
        <label htmlFor="tpl-bulk-flag-reason">Reason</label>
        <textarea
          id="tpl-bulk-flag-reason"
          data-testid="tpl-bulk-flag-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Annual review cycle"
        />
        <div className="tpl__modal__hint">
          {trimmed.length < 3
            ? `${3 - trimmed.length} more character${
                3 - trimmed.length === 1 ? "" : "s"
              } needed`
            : `Applied to every selected template's audit trail.`}
        </div>
      </div>
      {error !== null && (
        <div className="tpl__modal__error" role="alert">
          {error}
        </div>
      )}
      <div className="tpl__modal__ft">
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button
          onClick={() => {
            if (canSubmit) onConfirm(trimmed);
          }}
          disabled={!canSubmit}
          data-testid="tpl-bulk-flag-submit"
        >
          {busy ? "Flagging…" : `Flag ${noun}`}
        </Button>
      </div>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// BulkDeprecateModal
// ────────────────────────────────────────────────────────────────────

export type BulkDeprecateModalProps = {
  /** Open when the user clicked bulk Deprecate. */
  open: boolean;
  /** Selected rows eligible to deprecate (certified / re_certified). */
  count: number;
  /** Selected rows that AREN'T eligible and will be skipped. */
  skipped: number;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
};

export function BulkDeprecateModal({
  open,
  count,
  skipped,
  busy,
  error,
  onConfirm,
  onClose,
}: BulkDeprecateModalProps): JSX.Element | null {
  if (!open) return null;

  const noun = `${count} template${count === 1 ? "" : "s"}`;

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={`Deprecate ${noun}`}
      description="Deprecation disables these templates for NEW contracts but keeps the records — contracts and reviews already built from them stay intact and resolvable. This bulk action records no successor; to point a template at its replacement, deprecate it from its row instead."
    >
      {skipped > 0 && (
        <div className="tpl__modal__hint">
          {skipped} selected template{skipped === 1 ? "" : "s"}{" "}
          {skipped === 1 ? "isn't" : "aren't"} certified or re-certified and will
          be skipped — only those two states can be deprecated.
        </div>
      )}
      {error !== null && (
        <div className="tpl__modal__error" role="alert">
          {error}
        </div>
      )}
      <div className="tpl__modal__ft">
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button
          onClick={() => {
            if (!busy && count > 0) onConfirm();
          }}
          disabled={busy || count === 0}
          data-testid="tpl-bulk-deprecate-submit"
        >
          {busy ? "Deprecating…" : `Deprecate ${noun}`}
        </Button>
      </div>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// BulkExcludeModal — [S-718] opt several selected L1 templates out of
// this tenant's view at once. Mirrors BulkDeprecateModal's eligible/
// skipped split: only L1 rows not already excluded count toward the
// action (exclude() is idempotent per-row anyway, but showing an
// accurate "will actually change" count matches the deprecate UX).
// ────────────────────────────────────────────────────────────────────

export type BulkExcludeModalProps = {
  /** Open when the user clicked bulk Exclude. */
  open: boolean;
  /** Selected rows eligible to exclude (L1, not already excluded). */
  count: number;
  /** Selected rows that AREN'T eligible (L2, or already excluded) and will be skipped. */
  skipped: number;
  busy: boolean;
  error: string | null;
  onConfirm: (reason: string | null) => void;
  onClose: () => void;
};

export function BulkExcludeModal({
  open,
  count,
  skipped,
  busy,
  error,
  onConfirm,
  onClose,
}: BulkExcludeModalProps): JSX.Element | null {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  if (!open) return null;

  const noun = `${count} template${count === 1 ? "" : "s"}`;
  const trimmed = reason.trim();

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={`Exclude ${noun} from this tenant`}
      description="Hides the selected L1 templates from this tenant's certified library and search — the platform template itself is unaffected, and any tenant admin can re-include it later."
    >
      {skipped > 0 && (
        <div className="tpl__modal__hint">
          {skipped} selected template{skipped === 1 ? "" : "s"}{" "}
          {skipped === 1 ? "isn't" : "aren't"} eligible and will be skipped —
          only L1 templates not already excluded can be excluded here.
        </div>
      )}
      <div className="tpl__modal__row">
        <label htmlFor="tpl-bulk-exclude-reason">Reason (optional)</label>
        <textarea
          id="tpl-bulk-exclude-reason"
          data-testid="tpl-bulk-exclude-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Doesn't match our jurisdiction's requirements"
        />
      </div>
      {error !== null && (
        <div className="tpl__modal__error" role="alert">
          {error}
        </div>
      )}
      <div className="tpl__modal__ft">
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button
          onClick={() => {
            if (!busy && count > 0) onConfirm(trimmed.length > 0 ? trimmed : null);
          }}
          disabled={busy || count === 0}
          data-testid="tpl-bulk-exclude-submit"
        >
          {busy ? "Excluding…" : `Exclude ${noun}`}
        </Button>
      </div>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// DeprecateTemplateModal
// ────────────────────────────────────────────────────────────────────

export type DeprecateTemplateModalProps = {
  /** Template being deprecated; null hides the dialog. */
  template: CertifiedTemplateRow | null;
  /** All currently-rendered templates — used to populate the
   *  successor picker. The modal filters this list to the eligible
   *  successors (same layer + tenant + certified/re_certified). */
  siblings: ReadonlyArray<CertifiedTemplateRow>;
  onClose: () => void;
};

export function DeprecateTemplateModal({
  template,
  siblings,
  onClose,
}: DeprecateTemplateModalProps): JSX.Element | null {
  const deprecate = useDeprecate();
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [asOf, setAsOf] = useState(todayISO);
  const [successorId, setSuccessorId] = useState<string>("");

  useEffect(() => {
    if (template === null) return;
    setAsOf(new Date().toISOString().slice(0, 10));
    setSuccessorId("");
    deprecate.reset();
  }, [template?.id]);

  const validSuccessors = useMemo(() => {
    if (template === null) return [] as CertifiedTemplateRow[];
    return siblings.filter(
      (s) =>
        s.id !== template.id &&
        s.layerSource === template.layerSource &&
        s.tenantId === template.tenantId &&
        (s.lifecycleState === "certified" ||
          s.lifecycleState === "re_certified")
    );
  }, [siblings, template]);

  // [S-224] Cascade-preview counts. The hook is invoked unconditionally
  // (rules-of-hooks) — null template just disables the underlying
  // query. Tenant-scoped template (L2) routes through that tenant; L1
  // hits the platform_admin-only cross-tenant count.
  const dependents = useTemplateDependents(
    template?.id,
    template?.layerSource ?? "L1",
    // For L2, the tenantId comes off the template (`tenant-xyz`); we
    // need just the raw studio id, which the route accepts.
    template?.tenantId !== undefined && template?.tenantId !== null
      ? template.tenantId.replace(/^tenant-/, "")
      : ""
  );

  if (template === null) return null;

  // Date input gives ISO yyyy-mm-dd; we widen to a full ISO datetime
  // at noon-local so the day boundary doesn't shift under timezone math.
  const asOfIso = asOf.length === 10 ? new Date(`${asOf}T12:00:00`).toISOString() : "";
  const canSubmit = asOfIso !== "" && !deprecate.isPending;

  const close = (): void => {
    deprecate.reset();
    onClose();
  };

  const submit = (): void => {
    if (!canSubmit) return;
    deprecate.mutate(
      {
        templateId: template.id,
        tenantId: template.tenantId,
        asOf: asOfIso,
        successorId: successorId.length > 0 ? successorId : null,
      },
      {
        onSuccess: () => {
          deprecate.reset();
          onClose();
        },
      }
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) close();
      }}
      title={`Deprecate ${prettyLabel(template)}`}
      description={`Stops appearing in the certified templates after the effective date. Drafters still see it linked from existing contracts; new contracts can't resolve to it.`}
    >
      <div
        className="tpl__modal__row tpl__modal__row--cascade"
        data-testid="tpl-dep-cascade"
      >
        <CascadeBanner query={dependents} />
      </div>
      <div className="tpl__modal__row">
        <label htmlFor="tpl-dep-as-of">Effective from</label>
        <input
          id="tpl-dep-as-of"
          data-testid="tpl-dep-as-of"
          type="date"
          value={asOf}
          min={todayISO}
          onChange={(e) => setAsOf(e.target.value)}
        />
        <div className="tpl__modal__hint">
          Defaults to today. Future dates are allowed for scheduled
          deprecations.
        </div>
      </div>
      {validSuccessors.length > 0 && (
        <div className="tpl__modal__row">
          <label>Successor (optional)</label>
          <Select<string>
            label="Successor"
            value={successorId.length > 0 ? successorId : undefined}
            onValueChange={(v) => setSuccessorId(v ?? "")}
            clearable
            placeholder="— no successor —"
            options={validSuccessors.map((s) => ({
              value: s.id,
              label: prettyLabel(s),
            }))}
          />
          <div className="tpl__modal__hint">
            Drafters resolving against the deprecated template are nudged
            toward the successor in the Templates UI.
          </div>
        </div>
      )}
      {validSuccessors.length === 0 && (
        <div className="tpl__modal__hint">
          No eligible successor in this layer — deprecating without one is
          fine; new contracts will simply have no template anchor.
        </div>
      )}
      {deprecate.isError && (
        <div className="tpl__modal__error" role="alert">
          {deprecate.error?.message ?? "Failed to deprecate template"}
        </div>
      )}
      <div className="tpl__modal__ft">
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button
          onClick={submit}
          disabled={!canSubmit}
          data-testid="tpl-dep-submit"
        >
          {deprecate.isPending ? "Deprecating…" : "Deprecate template"}
        </Button>
      </div>
    </Dialog>
  );
}

// [S-224] Cascade banner — renders the open-Review + open-Brief
// counts so the admin sees the blast radius before confirming.
// States:
//   - loading: "Checking dependents…"
//   - error / unknown: warning that counts couldn't be fetched
//   - zero: green "no dependent reviews / briefs" line
//   - non-zero: amber "N reviews + M briefs reference this template" line
function CascadeBanner({
  query,
}: {
  query: ReturnType<typeof useTemplateDependents>;
}): JSX.Element {
  if (query.isLoading) {
    return (
      <div className="tpl__modal__hint" data-testid="tpl-dep-cascade-loading">
        Checking dependents…
      </div>
    );
  }
  if (query.isError || query.data === undefined) {
    return (
      <div
        className="tpl__modal__hint"
        data-testid="tpl-dep-cascade-unknown"
        role="status"
      >
        Couldn't compute dependents. Deprecation will still proceed; existing
        reviews + briefs keep their template references.
      </div>
    );
  }
  const { openReviews, openBriefs } = query.data;
  const total = openReviews + openBriefs;
  if (total === 0) {
    return (
      <div
        className="tpl__modal__hint tpl__modal__hint--ok"
        data-testid="tpl-dep-cascade-empty"
      >
        No reviews or briefs reference this template. Safe to deprecate.
      </div>
    );
  }
  return (
    <div
      className="tpl__modal__hint tpl__modal__hint--warn"
      data-testid="tpl-dep-cascade-warn"
      role="alert"
    >
      <strong>Cascade preview:</strong>{" "}
      {pluralize(openReviews, "review")} and{" "}
      {pluralize(openBriefs, "brief")} reference this template. They'll keep
      their existing anchor; new reviews + briefs will not be able to resolve
      to it after the effective date.
    </div>
  );
}

function pluralize(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

// ────────────────────────────────────────────────────────────────────
// [S-221 follow-up] AssignPeerReviewerModal
// Lead attestor opens this from CertificationQueue / TemplateDetail
// QuickActions on a draft or under_review template. The form accepts
// the peer reviewer's uid (a free-text input — the directory picker
// is a separate, much-larger story). 4-eyes is server-enforced; we
// disable submit when the entered uid matches the current caller to
// give immediate feedback.
// ────────────────────────────────────────────────────────────────────

export type AssignPeerReviewerModalProps = {
  template: CertifiedTemplateRow | null;
  /** Current caller's uid; used to block 4-eyes self-assignment. */
  callerUid: string;
  onClose: () => void;
};

export function AssignPeerReviewerModal({
  template,
  callerUid,
  onClose,
}: AssignPeerReviewerModalProps): JSX.Element | null {
  const assign = useAssignPeerReviewer();
  const [uid, setUid] = useState("");

  useEffect(() => {
    if (template === null) return;
    setUid(template.peerReviewerUid ?? "");
    assign.reset();
  }, [template?.id]);

  if (template === null) return null;

  const trimmed = uid.trim();
  const isSelf = trimmed.length > 0 && trimmed === callerUid;
  const canSubmit = trimmed.length >= 1 && !isSelf && !assign.isPending;

  const close = (): void => {
    assign.reset();
    onClose();
  };

  const submit = (): void => {
    if (!canSubmit) return;
    assign.mutate(
      {
        templateId: template.id,
        tenantId: template.tenantId,
        peerReviewerUid: trimmed,
      },
      {
        onSuccess: () => {
          assign.reset();
          onClose();
        },
      }
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) close();
      }}
      title={`Assign peer reviewer for ${prettyLabel(template)}`}
      description="The peer reviewer is a second attorney who reviews this template before it certifies. They MUST be different from you (4-eyes principle). Reassigning resets any prior peer-review timestamp."
    >
      <div className="tpl__modal__row">
        <label htmlFor="tpl-peer-uid">Peer reviewer uid</label>
        <input
          id="tpl-peer-uid"
          data-testid="tpl-peer-uid"
          type="text"
          value={uid}
          onChange={(e) => setUid(e.target.value)}
          placeholder="e.g. lwilson@contractstudio.example"
          autoComplete="off"
        />
        <div className="tpl__modal__hint">
          {trimmed.length === 0
            ? "Required — enter the attorney's account uid."
            : isSelf
              ? "Cannot assign yourself — peer review is a 4-eyes check."
              : template.peerReviewerUid === trimmed
                ? "Same reviewer as currently assigned."
                : "Saved on the template's audit trail."}
        </div>
      </div>
      {assign.isError && (
        <div className="tpl__modal__error" role="alert">
          {assign.error?.message ?? "Failed to assign peer reviewer"}
        </div>
      )}
      <div className="tpl__modal__ft">
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button
          onClick={submit}
          disabled={!canSubmit}
          data-testid="tpl-peer-submit"
        >
          {assign.isPending ? "Assigning…" : "Assign peer reviewer"}
        </Button>
      </div>
    </Dialog>
  );
}
